import styles from './ProfilePage.module.css';
import { useUser } from '../../../Context/useUser';
import { useEffect, useRef, useState } from 'react';
import API from '../../../Utils/api';

import Avatar from 'react-avatar';
import { useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import { toast } from 'react-toastify';

import { FaCreditCard } from 'react-icons/fa';
import { FaLocationDot, FaBuildingColumns } from 'react-icons/fa6';
import { IoNotifications } from 'react-icons/io5';
import { MdOutlineSecurity } from 'react-icons/md';
import { HiOutlineIdentification } from 'react-icons/hi';
import { BsCashCoin } from 'react-icons/bs';
import { BsBank } from 'react-icons/bs';
import { FaRegCreditCard } from 'react-icons/fa6';

import { Badge, Button } from 'react-bootstrap';
import Table from '../../../Components/Table/Table';
import { useForm } from 'react-hook-form';
import { getProfileSaveFeedback, stripSetupEmailStatus } from './emailUpdateFeedback';

const targetTimeTransform = obj => {
    let times = 0;
    if (obj?.hours && !isNaN(obj?.hours)) times += 3600 * obj.hours;
    if (obj?.minutes && !isNaN(obj?.minutes)) times += 60 * obj.minutes;
    if (obj?.seconds && !isNaN(obj?.seconds)) times += obj.seconds;
    return times;
};

const userTargetTimeTransform = targetTime => {
    if (!targetTime || typeof targetTime !== 'number') return targetTime;

    const obj = {};

    if (targetTime >= 3600) {
        obj.hours = Math.floor(targetTime / 3600);
        targetTime = targetTime % 3600;
    }

    obj.minutes = Math.floor(targetTime / 60);
    obj.seconds = targetTime % 60;

    return obj;
};

// The rail settings nav and the main header's current-section label read from this one list.
const SECTIONS = [
    { key: 'profile', label: 'Profile', Icon: HiOutlineIdentification },
    { key: 'security', label: 'Security', Icon: MdOutlineSecurity },
    { key: 'billing', label: 'Billing', Icon: FaCreditCard },
    { key: 'notifications', label: 'Notifications', Icon: IoNotifications }
];

// Fields the profile form edits. targetTime has no control yet but is cleared by an athlete-type change.
const PROFILE_FIELDS = [
    'title',
    'firstName',
    'lastName',
    'birthYear',
    'gender',
    'email',
    'phoneNumber',
    'location',
    'swimmingClub',
    'athleteType',
    'athleteLevel',
    'preferredDistance',
    'preferredStyle',
    'targetTime',
    'height',
    'weight',
    'sittingHeight',
    'handOpening'
];

const toProfileDraft = source => ({
    ...Object.fromEntries(PROFILE_FIELDS.map(field => [field, source[field]])),
    targetTime: userTargetTimeTransform(source.targetTime)
});

// Inputs hold strings, the loaded user may hold numbers or null: compare them as the controls show them.
const asControlValue = value =>
    value === null || value === undefined ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);

const hasDraftChanges = (draft, source) => {
    const savedDraft = toProfileDraft(source);
    return PROFILE_FIELDS.some(field => asControlValue(draft[field]) !== asControlValue(savedDraft[field]));
};

const GENDER_OPTIONS = [
    { value: '', label: 'Select Gender' },
    { value: 'Female', label: 'Female' },
    { value: 'Male', label: 'Male' },
    { value: 'Other', label: 'Other' }
];

const ATHLETE_TYPE_OPTIONS = [
    { value: '', label: 'Select Type' },
    { value: 'Pool', label: 'Pool' },
    { value: 'Open Water', label: 'Open Water' }
];

const ATHLETE_LEVEL_OPTIONS = [
    { value: '', label: 'Select Level' },
    { value: 'Young', label: 'Young' },
    { value: 'Adolescent', label: 'Adolescent' },
    { value: 'Elite', label: 'Elite' },
    { value: 'Master', label: 'Master' }
];

const DISTANCE_OPTIONS = {
    Pool: ['50', '100', '200', '400', '800', '1500'],
    'Open Water': ['750', '1500', '3000', '5000', '10000', '30000']
};

const STYLE_OPTIONS = [
    { value: '', label: 'Select Style' },
    { value: 'Freestyle', label: 'Freestyle' },
    { value: 'Backstroke', label: 'Backstroke' },
    { value: 'BreastStroke', label: 'Breaststroke' },
    { value: 'Butterfly', label: 'Butterfly' },
    { value: 'IM', label: 'Individual Medley' }
];

const renderOptions = options =>
    options.map(opt => (
        <option key={opt.value} value={opt.value}>
            {opt.label}
        </option>
    ));

const computeBiometrics = ({ height, weight, birthYear }) => {
    const watchedHeight = Number(height || null);
    const watchedWeight = Number(weight || null);
    const watchedBirthYear = Number(birthYear || null);

    const age =
        watchedBirthYear && watchedBirthYear >= 1900
            ? new Date().getFullYear() - watchedBirthYear
            : null;

    const bmp =
        watchedWeight && watchedHeight && watchedBirthYear && age !== null
            ? (10 * watchedWeight + 6.25 * watchedHeight - 5 * age + 5).toFixed(2)
            : 'Not set';

    const bmi =
        watchedWeight && watchedHeight && watchedHeight !== 0
            ? (watchedWeight / ((watchedHeight / 100) * (watchedHeight / 100))).toFixed(2)
            : 'Not set';

    const hrMax = watchedBirthYear && watchedBirthYear >= 1900 && age !== null ? 220 - age : 'Not set';

    return { age, bmp, bmi, hrMax };
};

export default function ProfilePage() {
    const { user, setUser } = useUser();
    const { athleteId } = useParams();
    const visitedUser = useVisitingUser(athleteId);
    const [currentUser, setCurrentUser] = useState(null);
    const [profileDraft, setProfileDraft] = useState(null);
    const [athleteStats, setAthleteStats] = useState(null);
    const [isSavingProfile, setIsSavingProfile] = useState(false);

    // TODO: Split these into separate components for each view mode
    const [viewMode, setViewMode] = useState('profile'); // 'security', 'billing', 'notifications'

    const { register, handleSubmit, reset } = useForm({
        defaultValues: {
            changesToAccount: user?.notifications?.changesToAccount ?? false,
            promotionalOffers: user?.notifications?.promotionalOffers ?? false
        }
    });

    const {
        register: registerPassword,
        handleSubmit: handlePasswordSubmit,
        reset: resetPasswordForm,
        formState: { errors: passwordErrors, isSubmitting }
    } = useForm();

    const onPasswordChange = async ({ currentPassword, newPassword, confirmPassword }) => {
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match.');
            return;
        }
        try {
            await API.updatePassword({
                userId: currentUser._id,
                oldPassword: currentPassword,
                newPassword
            });
            toast.success('Password updated successfully.');
            resetPasswordForm();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Failed to update password.');
        }
    };

    useEffect(() => {
        if (currentUser) {
            reset({
                changesToAccount: currentUser.notifications?.changesToAccount ?? false,
                promotionalOffers: currentUser.notifications?.promotionalOffers ?? false
            });
        }
    }, [currentUser, reset]);

    useEffect(() => {
        setCurrentUser(athleteId ? visitedUser : user);
    }, [visitedUser, user, athleteId]);

    // The draft is (re)initialised from every loaded or saved user, except that an unsaved draft for the same
    // user survives saves that do not touch it (e.g. notifications); typing only ever touches the draft.
    const draftSource = useRef(null);
    useEffect(() => {
        const previous = draftSource.current;
        draftSource.current = currentUser;
        setProfileDraft(draft =>
            draft && previous && currentUser?._id === previous._id && hasDraftChanges(draft, previous)
                ? draft
                : currentUser
                  ? toProfileDraft(currentUser)
                  : null
        );
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser?._id) return;
        const fetchStats = async () => {
            try {
                const { data } = await API.getWorkoutStatsByUserId(currentUser._id);
                setAthleteStats(data);
            } catch (e) {
                setAthleteStats(null);
            }
        };
        fetchStats();
    }, [currentUser?._id]);

    // Shared by the profile form and the notifications form; each passes only its own changes.
    // Resolves to the saved user, or null when the save failed.
    const persistUser = async nextUser => {
        let saveFeedback = getProfileSaveFeedback();
        let savedUser;
        try {
            const payload = {
                ...nextUser,
                targetTime: targetTimeTransform(userTargetTimeTransform(nextUser.targetTime))
            };
            if (!payload.gender) {
                delete payload.gender;
            }
            if (user._id === currentUser._id) {
                const { data } = await API.updateMyself(payload);
                setUser({ ...user, ...data });
                savedUser = { ...data, targetTime: userTargetTimeTransform(data.targetTime) };
            } else {
                const { data } = await API.updateUser(currentUser._id, payload);
                savedUser = {
                    ...stripSetupEmailStatus(data),
                    targetTime: userTargetTimeTransform(data.targetTime)
                };
                saveFeedback = getProfileSaveFeedback(data);
            }
            setCurrentUser(savedUser);
            toast[saveFeedback.type](saveFeedback.message);
            return savedUser;
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Failed to save profile.');
            return null;
        }
    };

    const saveProfile = async event => {
        event.preventDefault();
        if (!currentUser?._id) return;
        setIsSavingProfile(true);
        try {
            const savedUser = await persistUser({ ...currentUser, ...profileDraft });
            if (savedUser) setProfileDraft(toProfileDraft(savedUser));
        } finally {
            setIsSavingProfile(false);
        }
    };

    const onNotificationFormSubmit = data => {
        // Convert checkbox values to booleans
        const updatedNotifications = {
            changesToAccount: !!data.changesToAccount,
            promotionalOffers: !!data.promotionalOffers
        };
        return persistUser({
            ...currentUser,
            notifications: {
                ...currentUser.notifications,
                ...updatedNotifications
            }
        });
    };

    if (!currentUser || !profileDraft) return;

    const updateDraft = field => event => {
        const { value } = event.target;
        setProfileDraft(draft => ({ ...draft, [field]: value }));
    };

    // If the type changes, clear the preferred distance and target time that belonged to the old type.
    const changeAthleteType = event => {
        const { value } = event.target;
        setProfileDraft(draft => {
            const shouldClearDistanceAndTime = draft.athleteType && draft.athleteType !== value;
            return {
                ...draft,
                athleteType: value,
                preferredDistance: shouldClearDistanceAndTime ? null : draft.preferredDistance,
                targetTime: shouldClearDistanceAndTime ? null : draft.targetTime
            };
        });
    };

    const hasUnsavedChanges = hasDraftChanges(profileDraft, currentUser);

    // The rail summarises the saved user; the Biometrics group previews the draft.
    const saved = computeBiometrics(currentUser);
    const draft = computeBiometrics(profileDraft);

    // The API only accepts the change from an admin or an associated coach
    const canEditEmail = athleteId && (user?.type === 'Admin' || user?.type === 'Coach');

    const draftValue = field => profileDraft[field] ?? '';
    const distanceOptions = DISTANCE_OPTIONS[profileDraft.athleteType];

    const paymentColumns = [
        {
            Header: 'Transaction ID',
            accessor: 'Transaction Id'
        },
        { Header: 'Payment Date', accessor: 'date' },
        {
            Header: 'Amount (€)',
            accessor: 'Amount'
        },
        {
            Header: 'Payment Status',
            accessor: 'Status',
            Cell: ({ value }) => (
                <div>
                    <Badge pill bg='success'>
                        Paid
                    </Badge>
                </div>
            )
        }
    ];

    const activeSection = SECTIONS.find(section => section.key === viewMode);

    return (
        <div className={styles.pageWrap}>
            <aside className={styles.leftSide}>
                <div className={styles.identity}>
                    <Avatar
                        name={`${currentUser.firstName} ${currentUser.lastName}`}
                        round={true}
                    />
                    <div className={styles.identityText}>
                        <div className={styles.name}>
                            {currentUser.title} {currentUser.firstName} {currentUser.lastName}
                        </div>
                        <div className={styles.email}>{currentUser.email}</div>
                    </div>
                </div>
                <div className={styles.meta}>
                    <div className={styles.metaRow} title='Location'>
                        <FaLocationDot className={styles.metaIcon} />{' '}
                        {currentUser?.location ?? 'Not Provided'}
                    </div>
                    <div className={styles.metaRow} title='Swimming Club'>
                        <FaBuildingColumns className={styles.metaIcon} />{' '}
                        {currentUser.swimmingClub ?? 'Not Provided'}
                    </div>
                </div>
                <nav className={styles.settingsNav} aria-label='Profile settings'>
                    {SECTIONS.map(({ key, label, Icon }) => (
                        <button
                            key={key}
                            type='button'
                            className={`${styles.navBtn} ${viewMode === key ? styles.active : ''}`}
                            aria-current={viewMode === key ? 'page' : undefined}
                            onClick={() => setViewMode(key)}
                        >
                            <Icon size={18} aria-hidden='true' />
                            <span>{label}</span>
                        </button>
                    ))}
                </nav>
                <div className={styles.about}>
                    <div className={styles.aboutTitle}>About</div>
                    <div className={styles.aboutCntent}>
                        <div className={styles.statRow}>
                            <b>Age:</b> <span>{saved.age}</span>
                        </div>
                        <div className={styles.statRow}>
                            <b>BMP:</b> <span>{saved.bmp}</span>
                        </div>
                        <div className={styles.statRow}>
                            <b>BMI:</b> <span>{saved.bmi}</span>
                        </div>
                    </div>
                    <div className={styles.aboutCntent}>
                        <div className={styles.statRow}>
                            <b>Total Workouts Completed:</b>{' '}
                            <span className={styles.statCount}>
                                {athleteStats?.workouts?.Workout?.Completed ?? 0}
                            </span>
                        </div>
                        <div className={styles.statRow}>
                            <b>Total Races:</b>{' '}
                            <span className={styles.statCount}>
                                {athleteStats?.workouts?.Race?.Completed ?? 0}
                            </span>
                        </div>
                        <div className={styles.statRow}>
                            <b>Total Tests:</b>{' '}
                            <span className={styles.statCount}>
                                {athleteStats?.tests?.total ?? 0}
                            </span>
                        </div>
                    </div>
                    <div className={styles.aboutCntent}>
                        <div
                            className={styles.statRow}
                            title={currentUser.currentCSS || 'No CSS has been recorded for the user'}
                        >
                            <b>Critical Swim Speed (CSS):</b>{' '}
                            <span>
                                {currentUser?.currentCSS?.toFixed(2) === undefined
                                    ? 'N/A'
                                    : currentUser?.currentCSS?.toFixed(2)}
                            </span>
                        </div>
                        <div className={styles.statRow}>
                            <b>Maximum Swim Speed (MSS):</b>{' '}
                            <span>
                                {currentUser?.currentMSS?.toFixed(2) === undefined
                                    ? 'N/A'
                                    : currentUser?.currentMSS?.toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div>
            </aside>
            <div className={styles.rightSide}>
                <h1 className={styles.srOnly}>{activeSection.label}</h1>
                <div className={styles.selectedMenuItem}>
                    {viewMode === 'profile' && (
                        <form className={styles.profileForm} onSubmit={saveProfile}>
                            <section className={styles.panel} aria-labelledby='profile-personal'>
                                <h2 id='profile-personal' className={styles.groupTitle}>
                                    Personal details
                                </h2>
                                <div className={styles.fieldGrid}>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-title'>
                                            Title
                                        </label>
                                        <input
                                            id='pf-title'
                                            className={styles.control}
                                            type='text'
                                            autoComplete='honorific-prefix'
                                            value={draftValue('title')}
                                            onChange={updateDraft('title')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-firstName'>
                                            First name
                                        </label>
                                        <input
                                            id='pf-firstName'
                                            className={styles.control}
                                            type='text'
                                            autoComplete='given-name'
                                            value={draftValue('firstName')}
                                            onChange={updateDraft('firstName')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-lastName'>
                                            Last name
                                        </label>
                                        <input
                                            id='pf-lastName'
                                            className={styles.control}
                                            type='text'
                                            autoComplete='family-name'
                                            value={draftValue('lastName')}
                                            onChange={updateDraft('lastName')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-birthYear'>
                                            Birth year
                                        </label>
                                        <input
                                            id='pf-birthYear'
                                            className={styles.control}
                                            type='number'
                                            inputMode='numeric'
                                            value={draftValue('birthYear')}
                                            onChange={updateDraft('birthYear')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-gender'>
                                            Gender
                                        </label>
                                        <select
                                            id='pf-gender'
                                            className={styles.control}
                                            value={draftValue('gender')}
                                            onChange={updateDraft('gender')}
                                        >
                                            {renderOptions(GENDER_OPTIONS)}
                                        </select>
                                    </div>
                                </div>
                            </section>
                            <section className={styles.panel} aria-labelledby='profile-contact'>
                                <h2 id='profile-contact' className={styles.groupTitle}>
                                    Contact &amp; club
                                </h2>
                                <div className={styles.fieldGrid}>
                                    {canEditEmail ? (
                                        <div className={styles.field}>
                                            <label className={styles.fieldLabel} htmlFor='pf-email'>
                                                Email
                                            </label>
                                            <input
                                                id='pf-email'
                                                className={styles.control}
                                                type='email'
                                                autoComplete='off'
                                                value={draftValue('email')}
                                                onChange={updateDraft('email')}
                                            />
                                        </div>
                                    ) : (
                                        <div className={styles.field}>
                                            <span className={styles.fieldLabel}>Email</span>
                                            <span className={styles.readValue}>{currentUser.email}</span>
                                        </div>
                                    )}
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-phoneNumber'>
                                            Phone number
                                        </label>
                                        <input
                                            id='pf-phoneNumber'
                                            className={styles.control}
                                            type='tel'
                                            autoComplete='tel'
                                            value={draftValue('phoneNumber')}
                                            onChange={updateDraft('phoneNumber')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-location'>
                                            Location
                                        </label>
                                        <input
                                            id='pf-location'
                                            className={styles.control}
                                            type='text'
                                            value={draftValue('location')}
                                            onChange={updateDraft('location')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-swimmingClub'>
                                            Swimming club
                                        </label>
                                        <input
                                            id='pf-swimmingClub'
                                            className={styles.control}
                                            type='text'
                                            value={draftValue('swimmingClub')}
                                            onChange={updateDraft('swimmingClub')}
                                        />
                                    </div>
                                </div>
                            </section>
                            <section className={styles.panel} aria-labelledby='profile-training'>
                                <h2 id='profile-training' className={styles.groupTitle}>
                                    Training profile
                                </h2>
                                <div className={styles.fieldGrid}>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-athleteType'>
                                            Athlete type
                                        </label>
                                        <select
                                            id='pf-athleteType'
                                            className={styles.control}
                                            value={draftValue('athleteType')}
                                            onChange={changeAthleteType}
                                        >
                                            {renderOptions(ATHLETE_TYPE_OPTIONS)}
                                        </select>
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-athleteLevel'>
                                            Athlete level
                                        </label>
                                        <select
                                            id='pf-athleteLevel'
                                            className={styles.control}
                                            value={draftValue('athleteLevel')}
                                            onChange={updateDraft('athleteLevel')}
                                        >
                                            {renderOptions(ATHLETE_LEVEL_OPTIONS)}
                                        </select>
                                    </div>
                                    {/* Target time stays out until the new time picker is available. */}
                                    {distanceOptions && (
                                        <>
                                            <p className={styles.subhead}>Favourite race</p>
                                            <div className={styles.field}>
                                                <label
                                                    className={styles.fieldLabel}
                                                    htmlFor='pf-preferredDistance'
                                                >
                                                    Race distance
                                                </label>
                                                <select
                                                    id='pf-preferredDistance'
                                                    className={styles.control}
                                                    value={draftValue('preferredDistance')}
                                                    onChange={updateDraft('preferredDistance')}
                                                >
                                                    {renderOptions([
                                                        { value: '', label: 'Select Distance' },
                                                        ...distanceOptions.map(value => ({ value, label: value }))
                                                    ])}
                                                </select>
                                            </div>
                                            {profileDraft.athleteType === 'Pool' && (
                                                <div className={styles.field}>
                                                    <label
                                                        className={styles.fieldLabel}
                                                        htmlFor='pf-preferredStyle'
                                                    >
                                                        Race style
                                                    </label>
                                                    <select
                                                        id='pf-preferredStyle'
                                                        className={styles.control}
                                                        value={draftValue('preferredStyle')}
                                                        onChange={updateDraft('preferredStyle')}
                                                    >
                                                        {renderOptions(STYLE_OPTIONS)}
                                                    </select>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </section>
                            <section className={styles.panel} aria-labelledby='profile-biometrics'>
                                <h2 id='profile-biometrics' className={styles.groupTitle}>
                                    Biometrics
                                </h2>
                                <div className={styles.fieldGrid}>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-height'>
                                            Height (cm)
                                        </label>
                                        <input
                                            id='pf-height'
                                            className={styles.control}
                                            type='number'
                                            inputMode='decimal'
                                            value={draftValue('height')}
                                            onChange={updateDraft('height')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-weight'>
                                            Weight (kg)
                                        </label>
                                        <input
                                            id='pf-weight'
                                            className={styles.control}
                                            type='number'
                                            inputMode='decimal'
                                            value={draftValue('weight')}
                                            onChange={updateDraft('weight')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-sittingHeight'>
                                            Sitting height (cm)
                                        </label>
                                        <input
                                            id='pf-sittingHeight'
                                            className={styles.control}
                                            type='number'
                                            inputMode='decimal'
                                            value={draftValue('sittingHeight')}
                                            onChange={updateDraft('sittingHeight')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.fieldLabel} htmlFor='pf-handOpening'>
                                            Hand opening (cm)
                                        </label>
                                        <input
                                            id='pf-handOpening'
                                            className={styles.control}
                                            type='number'
                                            inputMode='decimal'
                                            value={draftValue('handOpening')}
                                            onChange={updateDraft('handOpening')}
                                        />
                                    </div>
                                    <div className={styles.field}>
                                        <span className={styles.fieldLabel}>BMP (Basic Metabolic Panel)</span>
                                        <span className={styles.readValue}>{draft.bmp}</span>
                                    </div>
                                    <div className={styles.field}>
                                        <span className={styles.fieldLabel}>BMI (Body Mass Index)</span>
                                        <span className={styles.readValue}>{draft.bmi}</span>
                                    </div>
                                    <div className={styles.field}>
                                        <span className={styles.fieldLabel}>Hr max</span>
                                        <span className={styles.readValue}>{draft.hrMax}</span>
                                    </div>
                                </div>
                            </section>
                            <div className={styles.actionBar}>
                                <span
                                    className={`${styles.saveStatus} ${hasUnsavedChanges ? styles.dirty : ''}`}
                                    role='status'
                                >
                                    {hasUnsavedChanges ? (
                                        <>
                                            <span className={styles.unsavedDot} aria-hidden='true' />
                                            Unsaved changes
                                        </>
                                    ) : (
                                        'No unsaved changes'
                                    )}
                                </span>
                                <button
                                    type='submit'
                                    className='btn btn-primary'
                                    disabled={!hasUnsavedChanges || isSavingProfile}
                                >
                                    {isSavingProfile ? 'Saving...' : 'Save changes'}
                                </button>
                            </div>
                        </form>
                    )}
                    {viewMode === 'security' && (
                        <div className={styles.securityPage}>
                            <div className={styles.changePasswordArea}>
                                <div className={styles.header}>Change Password</div>
                                <form
                                    className={styles.changePasswordContent + ' form-group'}
                                    onSubmit={handlePasswordSubmit(onPasswordChange)}
                                >
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='currentPassword'>Current Password</label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='currentPassword'
                                            placeholder='Enter current password'
                                            {...registerPassword('currentPassword', {
                                                required: true
                                            })}
                                        />
                                        {passwordErrors.currentPassword && (
                                            <span className={styles.error}>
                                                Current password is required
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='newPassword'>New Password</label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='newPassword'
                                            placeholder='Enter new password'
                                            {...registerPassword('newPassword', {
                                                required: true,
                                                minLength: 5
                                            })}
                                        />
                                        {passwordErrors.newPassword && (
                                            <span className={styles.error}>
                                                New password is required (min 6 chars)
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.changePasswordField}>
                                        <label htmlFor='confirmPassword'>
                                            Confirm New Password
                                        </label>
                                        <input
                                            type='password'
                                            className='form-control'
                                            id='confirmPassword'
                                            placeholder='Confirm new password'
                                            {...registerPassword('confirmPassword', {
                                                required: true
                                            })}
                                        />
                                        {passwordErrors.confirmPassword && (
                                            <span className={styles.error}>
                                                Please confirm your new password
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        className={`${styles.changePasswordFormButton} btn btn-primary`}
                                        type='submit'
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit'}
                                    </button>
                                </form>
                            </div>
                            <div className={styles.disableAccountArea}>
                                <div className={styles.header}>Disable Account</div>
                                <div className={styles.disableAccountContent}>
                                    <div>
                                        Disable your account is a non permanent action and can only
                                        be undone by support. If you are sure you want to disable
                                        your account, select the button below.
                                    </div>
                                    <Button
                                        variant='outline-danger'
                                        onClick={() =>
                                            toast.error(
                                                'Feature temporarily disabled. Please contact support.'
                                            )
                                        }
                                    >
                                        I understand, disable my account
                                    </Button>{' '}
                                </div>
                            </div>
                        </div>
                    )}
                    {viewMode === 'billing' && (
                        <div className={styles.billingPage}>
                            <div className={styles.topCards}>
                                <div className={styles.billingCard}>
                                    <small>Current monthly bill</small>
                                    <span className={styles.billingCardSpan}>0€</span>
                                </div>
                                <div className={styles.billingCard}>
                                    <small>Next payment due</small>
                                    <span className={styles.billingCardSpan}>N/A</span>
                                </div>
                                <div className={styles.billingCard}>
                                    <small>Current plan</small>
                                    <span className={styles.billingCardSpan}>Free</span>
                                </div>
                            </div>
                            <div className={styles.paymentMethods}>
                                <section className={styles.panel} aria-labelledby='billing-methods'>
                                    <h2 id='billing-methods' className={styles.groupTitle}>
                                        Payment Methods
                                    </h2>
                                    <div className={`${styles.panelBody} ${styles.paymentMethodsArea}`}>
                                        <div className={styles.paymentMethodRow}>
                                            <span>
                                                <BsCashCoin size={17} style={{ marginRight: '5px' }} /> Cash
                                            </span>
                                            <Badge pill style={{ fontSize: '13px' }} bg='secondary'>
                                                Disabled
                                            </Badge>
                                        </div>
                                        <div className={styles.paymentMethodRow}>
                                            <span>
                                                <BsBank size={17} style={{ marginRight: '5px' }} />
                                                Bank Transfer
                                            </span>
                                            <Badge pill style={{ fontSize: '13px' }} bg='secondary'>
                                                Disabled
                                            </Badge>
                                        </div>
                                        <div className={styles.paymentMethodRow}>
                                            <span>
                                                <FaRegCreditCard size={17} style={{ marginRight: '5px' }} />
                                                Debit Card
                                            </span>
                                            <Badge pill style={{ fontSize: '13px' }} bg='secondary'>
                                                Disabled
                                            </Badge>
                                        </div>
                                    </div>
                                </section>
                                <section className={styles.panel} aria-labelledby='billing-history'>
                                    <h2 id='billing-history' className={styles.groupTitle}>
                                        Payment History
                                    </h2>
                                    <div className={styles.panelBody}>
                                        <Table
                                            columns={paymentColumns}
                                            data={[
                                                {
                                                    'Transaction Id': '#000',
                                                    date: '-',
                                                    Amount: '0.00',
                                                    Status: 'Paid'
                                                }
                                            ]}
                                        ></Table>
                                    </div>
                                </section>
                            </div>
                        </div>
                    )}
                    {viewMode === 'notifications' && (
                        <div className={styles.notificationsPage}>
                            <div className={styles.emailNotificationsArea}>
                                <div className={styles.emailNotificationsHeader}>
                                    Email Notifications
                                </div>
                                <div className={styles.emailNotificationsContent}>
                                    <div className={styles.emailNotificationField}>
                                        <span className={styles.notificationFieldLabel}>
                                            Default notification Email
                                        </span>
                                        <span className={styles.readValue}>{currentUser.email}</span>
                                    </div>
                                    <form onSubmit={handleSubmit(onNotificationFormSubmit)}>
                                        <div className={styles.emailNotificationField}>
                                            <label
                                                className={styles.notificationFieldLabel}
                                                htmlFor='changesToAccount'
                                            >
                                                Changes made to your account
                                            </label>
                                            <input
                                                type='checkbox'
                                                id='changesToAccount'
                                                {...register('changesToAccount')}
                                                defaultChecked={
                                                    currentUser.notifications?.changesToAccount ??
                                                    false
                                                }
                                            />{' '}
                                        </div>
                                        <div className={styles.emailNotificationField}>
                                            <label
                                                className={styles.notificationFieldLabel}
                                                htmlFor='promotionalOffers'
                                            >
                                                Marketing and Promotional Offers
                                            </label>
                                            <input
                                                type='checkbox'
                                                id='promotionalOffers'
                                                {...register('promotionalOffers')}
                                                defaultChecked={
                                                    currentUser.notifications?.promotionalOffers ??
                                                    false
                                                }
                                            />
                                        </div>
                                        <button
                                            type='submit'
                                            className={`${styles.changePasswordFormButton} btn btn-primary`}
                                        >
                                            Submit
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
